import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuthz } from "context/AuthzContext";
import { useClient } from "context/ClientContext";
import { listClientProfiles, apiRequest } from "services/tenantUserAdminService";

import { IoCheckmarkCircle, IoCloseCircle, IoWarning, IoRefresh, IoLockClosed } from "react-icons/io5";
import { FiClock } from "react-icons/fi";
import { TC } from "../../tokens/text";

// ─── Constants ────────────────────────────────────────────────────────────────

const CARD_BG = "linear-gradient(127.09deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)";
const CARD_BORDER = "1px solid rgba(255,255,255,0.08)";

const STATE_CONFIG = {
  open:         { label: "Open",          color: "#4ECDC4", icon: <FiClock size={14} /> },
  review_ready: { label: "Review Ready",  color: "#01B574", icon: <IoCheckmarkCircle size={14} /> },
  blocked:      { label: "Blocked",       color: "#f44336", icon: <IoCloseCircle size={14} /> },
  closed:       { label: "Closed",        color: "#636E72", icon: <IoLockClosed size={14} /> },
  reopened:     { label: "Reopened",      color: "#F7B731", icon: <IoWarning size={14} /> },
};

function StateBadge({ state }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.open;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: `${cfg.color}18`, color: cfg.color,
      border: `1px solid ${cfg.color}44`, borderRadius: 6,
      padding: "3px 10px", fontSize: 12, fontWeight: 700,
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function CheckRow({ label, passed, blocking }) {
  const color = passed ? "#01B574" : blocking ? "#f44336" : "#F7B731";
  return (
    <VuiBox display="flex" alignItems="center" gap={1.5} mb={1.2}>
      {passed
        ? <IoCheckmarkCircle color="#01B574" size={18} />
        : <IoCloseCircle color={color} size={18} />}
      <VuiTypography sx={{ color: passed ? "rgba(255,255,255,0.75)" : color, fontSize: 13, fontWeight: passed ? 400 : 600 }}>
        {label}
      </VuiTypography>
    </VuiBox>
  );
}

function ChecklistItem({ item }) {
  const statusColor = { done: "#01B574", pending: "#F7B731", blocked: "#f44336" };
  const c = statusColor[item.status] || "#636E72";
  return (
    <VuiBox display="flex" alignItems="flex-start" gap={1.5} mb={1}
      sx={{ background: `${c}0D`, border: `1px solid ${c}22`, borderRadius: 2, p: "8px 12px" }}>
      <span style={{ color: c, mt: 1, flexShrink: 0 }}>
        {item.status === "done" ? <IoCheckmarkCircle size={16} />
         : item.status === "blocked" ? <IoCloseCircle size={16} />
         : <IoWarning size={16} />}
      </span>
      <VuiBox>
        <VuiTypography sx={{ color: TC.primary, fontSize: 13, lineHeight: 1.4 }}>
          {item.label}
        </VuiTypography>
        {item.auto && (
          <VuiTypography sx={{ color: TC.secondary, fontSize: 11 }}>Automated check</VuiTypography>
        )}
      </VuiBox>
    </VuiBox>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PeriodClose() {
  const { tenantId } = useAuthz();
  const history = useHistory();
  const { pinnedClientId, pinClient } = useClient() || {};

  const [profiles, setProfiles]         = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [period, setPeriod]             = useState(null);
  const [loading, setLoading]           = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [error, setError]               = useState("");

  // Current period label
  const now = new Date();
  const currentPeriodId = now.toISOString().slice(0, 7);
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriodId);

  // Generate last 12 months for picker
  const periodOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return d.toISOString().slice(0, 7);
  });

  useEffect(() => {
    if (!tenantId) return;
    listClientProfiles({ tenantId, status: "active" })
      .then(d => {
        const list = d.clientProfiles || [];
        setProfiles(list);
        const nextSelected =
          (pinnedClientId && list.some((client) => client.id === pinnedClientId) && pinnedClientId) ||
          list[0]?.id ||
          "";
        if (nextSelected) setSelectedClient(nextSelected);
        if (nextSelected && nextSelected !== pinnedClientId) {
          const match = list.find((client) => client.id === nextSelected);
          pinClient?.(nextSelected, match?.legalName || match?.tradingName || nextSelected);
        }
      })
      .catch(() => {});
  }, [tenantId, pinnedClientId, pinClient]);

  const loadPeriod = useCallback(async () => {
    if (!tenantId || !selectedClient) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/periods/${selectedPeriod}`
      );
      setPeriod(data);
    } catch (e) {
      setError(e.message);
      setPeriod(null);
    } finally { setLoading(false); }
  }, [tenantId, selectedClient, selectedPeriod]);

  useEffect(() => { loadPeriod(); }, [loadPeriod]);

  const handleCheckReadiness = async () => {
    setCheckLoading(true);
    setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/periods/${selectedPeriod}/check-readiness`,
        { method: "POST" }
      );
      setPeriod(data);
    } catch (e) { setError(e.message); } finally { setCheckLoading(false); }
  };

  const handleClose = async () => {
    setCloseLoading(true);
    setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/periods/${selectedPeriod}/close`,
        { method: "POST" }
      );
      setPeriod(data);
      setConfirmOpen(false);
    } catch (e) {
      setError(e.message);
      setConfirmOpen(false);
    } finally { setCloseLoading(false); }
  };

  const handleReopen = async () => {
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/periods/${selectedPeriod}/reopen`,
        { method: "POST", body: { reason: "Accountant reopened from UI" } }
      );
      setPeriod(data);
    } catch (e) { setError(e.message); }
  };

  const client = profiles.find(p => p.id === selectedClient);
  const checks = period?.readiness_checks || {};
  const allPassed = Object.values(checks).every(Boolean);
  const isClosed = period?.state === "closed";
  const canClose = period?.state === "review_ready" && allPassed;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>📆 Period Close</VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              Review readiness checks and close accounting periods
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" gap={2} flexWrap="wrap">
            <Select value={selectedClient} onChange={e => {
              const next = e.target.value;
              setSelectedClient(next);
              const match = profiles.find((client) => client.id === next);
              pinClient?.(next, match?.legalName || match?.tradingName || next);
            }}
              sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, minWidth: 200, "& .MuiSvgIcon-root": { color: TC.primary }, fontSize: 13 }}>
              {profiles.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.legalName || p.tradingName || p.id}</MenuItem>
              ))}
            </Select>
            <Select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
              sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, minWidth: 130, "& .MuiSvgIcon-root": { color: TC.primary }, fontSize: 13 }}>
              {periodOptions.map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
            <VuiButton size="small" color="info" variant="outlined" startIcon={<IoRefresh size={14} />}
              onClick={loadPeriod} sx={{ fontSize: 12 }}>Refresh</VuiButton>
          </VuiBox>
        </VuiBox>

        {error && (
          <VuiBox mb={2} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "12px 16px" }}>
            <VuiTypography sx={{ color: "#f44336", fontSize: 13 }}>{error}</VuiTypography>
          </VuiBox>
        )}

        <Grid container spacing={3}>
          {/* Period Status Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                  Period Status
                </VuiTypography>

                {loading ? (
                  <LoadingSpinner size="sm" label="Loading…" />
                ) : period ? (
                  <>
                    <VuiBox mb={2}>
                      <VuiTypography sx={{ color: TC.primary, fontSize: 22, fontWeight: 800 }}>{selectedPeriod}</VuiTypography>
                      <VuiBox mt={1}><StateBadge state={period.state} /></VuiBox>
                    </VuiBox>

                    {period.closed_at && (
                      <VuiTypography sx={{ color: TC.secondary, fontSize: 12, mb: 2 }}>
                        Closed {new Date(period.closed_at).toLocaleDateString("en-GB")}
                      </VuiTypography>
                    )}

                    {period.blocking_reasons?.length > 0 && (
                      <VuiBox mb={2}>
                        {period.blocking_reasons.map((r, i) => (
                          <VuiBox key={i} display="flex" alignItems="center" gap={1} mb={0.8}
                            sx={{ background: "rgba(255,71,87,0.08)", border: "1px solid rgba(255,71,87,0.2)", borderRadius: 1.5, p: "6px 10px" }}>
                            <IoCloseCircle color="#f44336" size={14} />
                            <VuiTypography sx={{ color: "#f44336", fontSize: 12 }}>{r}</VuiTypography>
                          </VuiBox>
                        ))}
                      </VuiBox>
                    )}

                    <VuiBox display="flex" flexDirection="column" gap={1} mt={2}>
                      {!isClosed && (
                        <VuiButton fullWidth color="info" variant="outlined" size="small"
                          onClick={handleCheckReadiness} disabled={checkLoading}
                          sx={{ fontSize: 12 }}>
                          {checkLoading ? "Checking…" : "Run Readiness Check"}
                        </VuiButton>
                      )}
                      {canClose && (
                        <VuiButton fullWidth color="success" variant="contained" size="small"
                          onClick={() => setConfirmOpen(true)} sx={{ fontSize: 12, fontWeight: 700 }}>
                          Close Period ✓
                        </VuiButton>
                      )}
                      {isClosed && (
                        <VuiButton fullWidth color="warning" variant="outlined" size="small"
                          onClick={handleReopen} sx={{ fontSize: 12 }}>
                          Reopen Period
                        </VuiButton>
                      )}
                    </VuiBox>
                  </>
                ) : (
                  <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
                    {profiles.length === 0 ? "No clients available" : "No period data"}
                  </VuiTypography>
                )}
              </VuiBox>
            </Card>
          </Grid>

          {/* Readiness Checks */}
          <Grid item xs={12} md={4}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                  Readiness Checks
                </VuiTypography>
                {period ? (
                  <>
                    <CheckRow label="Bank feeds active" passed={!!checks.feeds_synced} blocking />
                    <CheckRow label="All exceptions resolved" passed={!!checks.exceptions_resolved} blocking />
                    <CheckRow label="No unreconciled balances" passed={!!checks.unreconciled_cleared} blocking />
                    <CheckRow label="VAT data complete" passed={!!checks.vat_complete} blocking={false} />

                    <VuiBox mt={2}>
                      <VuiProgress
                        value={Object.values(checks).filter(Boolean).length / 4 * 100}
                        color={allPassed ? "success" : "warning"}
                        sx={{ background: "#1a1f3a", height: 6 }}
                      />
                      <VuiTypography sx={{ color: TC.secondary, fontSize: 11, mt: 0.5, textAlign: "right" }}>
                        {Object.values(checks).filter(Boolean).length}/4 checks passed
                      </VuiTypography>
                    </VuiBox>
                  </>
                ) : (
                  <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
                    Run a readiness check to see results
                  </VuiTypography>
                )}
              </VuiBox>
            </Card>
          </Grid>

          {/* Close Checklist */}
          <Grid item xs={12} md={4}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                  Close Checklist
                </VuiTypography>
                {period?.checklist?.length > 0 ? (
                  period.checklist.map(item => <ChecklistItem key={item.id} item={item} />)
                ) : (
                  <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
                    Checklist generated after readiness check
                  </VuiTypography>
                )}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>

        {/* Confirm Close Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth
          aria-labelledby="period-close-dialog-title"
          aria-describedby="period-close-dialog-description"
          PaperProps={{ sx: { background: "rgba(15,18,59,0.98)", border: "1px solid #d8e6f7", borderRadius: "16px", color: TC.primary } }}>
          <DialogTitle id="period-close-dialog-title" sx={{ color: TC.primary }}>Close Period {selectedPeriod}?</DialogTitle>
          <DialogContent id="period-close-dialog-description">
            <VuiTypography sx={{ color: TC.midtone, fontSize: 14 }}>
              This will lock the period for <strong style={{ color: TC.primary }}>{client?.legalName || "this client"}</strong>.
              No new transactions will be posted to this period.
              You can reopen it if needed.
            </VuiTypography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <VuiButton variant="outlined" color="white" onClick={() => setConfirmOpen(false)} sx={{ fontSize: 12 }}>Cancel</VuiButton>
            <VuiButton variant="contained" color="success" onClick={handleClose} disabled={closeLoading} sx={{ fontSize: 12, fontWeight: 700 }}>
              {closeLoading ? "Closing…" : "Confirm Close"}
            </VuiButton>
          </DialogActions>
        </Dialog>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

